import { z } from 'zod'
import { ExchangeApiConfigurationException } from '../errors'
import { buildQueryString } from '../utils/buildQueryString'
import { createRestApiClient } from '../utils/createRestApiClient'
import { sign } from '../utils/sign'

const BASE_URL = 'https://api.bitget.com'

/** Bitget の error response body。 */
const errorBodySchema = z.object({
  code: z.string(),
  msg: z.string().optional(),
})

/**
 * Unified Account (UTA) へ移行済みのため Classic API (v2) を利用できないことを示す code。
 * 実 API で確認済み: `You are in Unified Account mode, and the Classic Account API is not supported`
 */
const UNIFIED_ACCOUNT_ERROR_CODE = '40085'

/**
 * Classic Account のままのため UTA API (v3) を利用できないことを示す code。
 * 実 API で確認済み: `You are in Classic Account mode, and the Unified Account API is not supported`
 */
const CLASSIC_ACCOUNT_ERROR_CODE = '40084'

/**
 * 署名検証に失敗したことを示す code 群。
 * 認証情報の誤りに加え、UTA アカウントで Classic API を呼んだ場合にも返る。
 */
const SIGNATURE_ERROR_CODES = new Set(['40009', '40010', '40011', '40012'])

/**
 * Bitget の error response を型付き例外へ分類する。
 * @param params - statusCode: HTTP status、errorMessage: response body の原文。
 * @returns 分類できた場合は設定不備例外、できない場合は undefined。
 */
export const classifyBitgetErrorResponse = ({
  errorMessage,
}: {
  readonly statusCode: number
  readonly errorMessage: string
}): Error | undefined => {
  let parsedBody: unknown
  try {
    parsedBody = JSON.parse(errorMessage)
  } catch {
    return undefined
  }

  const errorBody = errorBodySchema.safeParse(parsedBody)
  if (!errorBody.success) {
    return undefined
  }

  const { code, msg } = errorBody.data
  const detail = msg ?? code

  if (code === UNIFIED_ACCOUNT_ERROR_CODE) {
    return new ExchangeApiConfigurationException({
      reason: 'bitget-unified-account',
      detail,
    })
  }
  if (code === CLASSIC_ACCOUNT_ERROR_CODE) {
    return new ExchangeApiConfigurationException({
      reason: 'bitget-classic-account',
      detail,
    })
  }
  if (SIGNATURE_ERROR_CODES.has(code)) {
    return new ExchangeApiConfigurationException({
      reason: 'signature-verification-failed',
      detail,
    })
  }

  return undefined
}

const createAuthHeaders = async (params: {
  /** 認証用 API Key */
  apiKey: string
  /** 認証用 API Secret */
  apiSecret: string
  /** 認証用 API Passphrase */
  apiPassphrase: string
  /** request headers 作成関数 (関数内で作成する headers を上書きして強制適用させる header) */
  createHeaders?: () => Promise<Record<string, string>>
  dataString: string
  method: 'GET' | 'POST'
  path: string
}): Promise<Record<string, string>> => {
  const {
    apiKey,
    apiSecret,
    apiPassphrase,
    createHeaders,
    dataString,
    method,
    path,
  } = params

  // 署名対象文字列の作成
  const timestamp = Date.now()
  const signTarget = `${timestamp}${method}${path}${dataString}`

  // HMAC-SHA256署名の生成とbase64エンコード
  const signature = await sign({
    algorithm: 'sha256',
    payload: signTarget,
    secret: apiSecret,
    encoding: 'base64',
  })

  // 強制適用する request headers 作成
  const overrideHeaders = createHeaders ? await createHeaders() : {}

  return {
    'Content-Type': 'application/json',
    locale: 'en-US',
    'ACCESS-KEY': apiKey,
    'ACCESS-SIGN': signature,
    'ACCESS-TIMESTAMP': timestamp.toString(),
    'ACCESS-PASSPHRASE': apiPassphrase,
    ...overrideHeaders,
  }
}

export const createClient = ({
  apiKey,
  apiSecret,
  apiPassphrase,
  createHeaders,
  proxy,
}: {
  /** 認証用 API Key */
  apiKey: string
  /** 認証用 API Secret */
  apiSecret: string
  /** 認証用 API Passphrase */
  apiPassphrase: string
  /** request headers 作成関数 (関数内で作成する headers を上書きして強制適用させる header) */
  createHeaders?: () => Promise<Record<string, string>>
  /** request を proxy する場合の proxy base URL */
  proxy?: string
}) =>
  createRestApiClient({
    buildRequestData: async ({ method, path, query, body }) => {
      const queryString = buildQueryString(query)
      const queryWithQuestionMark = queryString ? `?${queryString}` : ''
      const bodyString = body ? JSON.stringify(body) : ''
      const url = `${BASE_URL}${path}${queryWithQuestionMark}`
      const dataString = queryWithQuestionMark + bodyString
      const headers = await createAuthHeaders({
        apiKey,
        apiSecret,
        apiPassphrase,
        createHeaders,
        dataString,
        method,
        path,
      })
      return {
        url,
        headers,
        body: method === 'POST' ? bodyString : undefined,
      }
    },
    getDelayForRetry: ({ statusCode }) => {
      // 401 エラーは 1秒待機してリトライ
      if (statusCode === 401) {
        return 1000
      }
      // 429 エラーは 10秒待機してリトライ
      if (statusCode === 429) {
        return 10_000
      }
      // 5xx エラーは 1秒待機してリトライ
      if (statusCode >= 500) {
        return 1000
      }
      // それ以外のエラーはリトライしない
      return undefined
    },
    classifyErrorResponse: classifyBitgetErrorResponse,
    proxy,
  })
